import React, {useState} from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import styled, {withTheme} from 'styled-components/macro';
import {Badge, Column, Icon, Row} from '../../../../shared';
import {_removeModal} from '../../../reducers/modals';
import history from '../../../../history';
import {goHome} from '../../../utils/utils';
import Image from '../../Image';
import {useSWRConfig} from 'swr';

Modal.setAppElement('#circleblack-root');

const _Header = styled(Row)``;

const _Section = styled(Row)`
  position: relative;
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  cursor: pointer;
`;

const getSectionIndicatorColors = (props) => {
  if (props.theme.dark) {
    return `
      background-color: ${props.isNav ? props.theme.primaryColor : 'rgba(255, 255, 255, 0.1)'};
    `;
  }
  return `
    background-color: ${props.isNav ? props.theme.primaryColor : 'rgba(0, 0, 0, 0.1)'};
  `;
};

const _SectionIndicator = styled.div`
  width: 5px;
  ${props => getSectionIndicatorColors(props)}
`;

const _SubSectionWrapper = styled(Column)`
  ${props => props.isSelected ? null : 'display: none;'}
  //noinspection CssInvalidPropertyValue
  height: -moz-fit-content;
  height: fit-content;
  overflow: visible;
`;

const _SubSection = styled(Row)`
  cursor: pointer;
`;

const _SectionLabel = styled.span`
  font-family: ${props => props.theme.titleText.name};
  font-size: ${props => props.theme.titleText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
  padding: 15px 0px 15px 15px;
`;

const _SubSectionLabel = styled.span`
  font-family: ${props => props.theme.titleText.name};
  font-size: ${props => props.theme.titleText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
  padding: 15px 0px 15px 0px;
`;

const trimPathName = (str) => {
  const arr = str.split('/');
  return arr[0] ? arr[0] : arr[1];
};

const MenuModal = (props) => {
  const {mutate} = useSWRConfig();
  const {size = 'sm', removeModal, content} = props;
  const path = trimPathName(history.location.pathname);

  const onClose = () => {
    removeModal(content);
  };

  const [openSection, setOpenSection] = useState({});
  const optionalSize = {
    top: '0px',
    bottom: '0px',
    left: '0',
    height: '100%',
    border: 'none',
    boxShadow: '1px 2px 8px rgb(0 0 0 / 50%)',
    inset: '0px',
    marginLeft: '0px',
    marginRight: 'auto',
    minWidth: '300px',
    overflow: 'hidden',
    transform: 'translate(0, 0)',
    boxSizing: 'border-box',
    backgroundColor: props.theme.tileBackgroundColor,
    backgroundImage: 'linear-gradient(to bottom right, rgba(0, 29, 40, 0.3), rgba(9, 16, 0, 0.3))',
    borderRadius: '0px',
    display: 'flex',
    flexDirection: 'column',
    padding: '0px',
    paddingBottom: '0px',
    filter: 'drop-shadow(5px 0px 4px rgba(0, 0, 0, 0.5))',
  };
  if (size) {
    switch (size) {
      case 'sm':
        optionalSize.maxWidth = '300px';
        break;
      case 'md':
        optionalSize.maxWidth = '600px';
        break;
      case 'lg':
        optionalSize.maxWidth = '1000px';
        break;
      default:
    }
  }

  return <Modal
    isOpen={true}
    onRequestClose={onClose}
    style={{
      overlay: {
        backgroundColor: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)',
        zIndex: 99,
      },
      content: {...optionalSize},
    }}>
    <_Header width={'100%'} height={'60px'} justifySubRow={'left'} alignSubRow={'middle'}>
      <Image maxWidth={'90px'} src={content.logo} maxHeight={'40px'} margin={'5px'} action={() => {
        goHome(content.role);
        removeModal(content);
      }}/>
    </_Header>
    <Column minHeight={'50px'} width={'fill'}>
      {content.sections && content.sections.map(section => <>
        <_Section width={'100%'}
                  alignSubRow={'stretch'}
                  alignItems={'stretch'}
                  justifySubRow={'space between'}
                  margin={'5px 0px'}
                  action={() => {
                    if (section.subsections.length === 1) {
                      if (section.subsections[0].page) {
                        if (window.location.pathname.startsWith('/analyze/dashboard') && section.subsections[0].label === 'Dashboard') {
                          mutate('defaultContainer', {force: true}, false);
                        }
                        history.push(section.subsections[0].page);
                        removeModal(content);
                      } else if (section.subsections[0].action) {
                        section.subsections[0].action(() => setOpenSection(section));
                      }
                    } else if (section.action) {
                      section.action(() => setOpenSection(section));
                    } else {
                      setOpenSection(section);
                    }

                    if (section.sideEffect) {
                      section.sideEffect();
                    }
                  }}
                  isSelected={openSection && openSection.label === section.label}
        >
          <_SectionLabel>{section.label}</_SectionLabel>
          <_SectionIndicator isNav={path === section.label.toLowerCase()}>&nbsp;</_SectionIndicator>
          {(section.label === 'Report')
            ? <Badge
              diameter='10px'
              types={['report']}
              position={{top: '10px', right: '10px'}}
              lastResize={section.lastResize}
              IsSubsectionBadge={true}/>
            : null}
          {(section.label === 'Compliance')
            ? <Badge
              diameter='10px'
              types={['compliance']}
              position={{top: '10px', right: '10px'}}
              lastResize={section.lastResize}
              IsSubsectionBadge={true}/>
            : null}
        </_Section>
        <_SubSectionWrapper isSelected={openSection && openSection.label === section.label}>
          {section.subsections && section.subsections.map(subsection => <_SubSection key={subsection.label}
                                                                                     justifySubRow={'left'}
                                                                                     alignSubRow={'middle'}
                                                                                     action={() => {
                                                                                       if (subsection.page) {
                                                                                         if (window.location.pathname.startsWith('/analyze/dashboard') && subsection.label === 'Dashboard') {
                                                                                           mutate('defaultContainer', {force: true}, false);
                                                                                         }
                                                                                         history.push(subsection.page);
                                                                                         setOpenSection({});
                                                                                         removeModal({type: 'MenuModal'});
                                                                                       } else if (subsection.action) {
                                                                                         subsection.action(() => {
                                                                                           setOpenSection({});
                                                                                           removeModal({type: 'MenuModal'});
                                                                                         });
                                                                                       }

                                                                                       if (subsection.sideEffect) {
                                                                                         subsection.sideEffect();
                                                                                       }
                                                                                     }}>
            {/* ToDo: replace this with 'L' icon. */}
            <Icon svg={'sidebarChevron'}/>
            <_SubSectionLabel>
              {subsection.label}
            </_SubSectionLabel>
          </_SubSection>)}
        </_SubSectionWrapper>
      </>)}
    </Column>
  </Modal>;
};

const mapDispatchToProps = dispatch => ({
  removeModal: modal => {
    dispatch(_removeModal(modal));
  },
});

export default connect(null, mapDispatchToProps)(withTheme(MenuModal));

MenuModal.propTypes = {
  /**
   * Title of modal
   */
  title: PropTypes.string.isRequired,
  /**
   * Function to call on Close
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Optional size of modal to specify width and max-width (sm = 300px, md = 500px, lg = 800px)
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  theme: PropTypes.object,
  removeModal: PropTypes.func.isRequired,
  content: PropTypes.object,
};
